import React from "react";
import { Tooltip } from "@vibe/core";
import { Alert, Check, Link, Remove, Warning } from "@vibe/icons";
import type { DiscrepancyState } from "./discrepancyGrid.interface";

/**
 * How one discrepancy state is presented in the grid's Status cell.
 *
 * `color` must be a Vibe semantic CSS custom property, never a literal hex — Vibe
 * redefines these per theme, and a frozen hex cannot follow a light/dark switch.
 * Available: --positive-color, --negative-color, --warning-color,
 * --primary-color, --secondary-text-color, --primary-text-color.
 */
export interface StateVisual {
  icon: React.ComponentType<any>;
  /** A `var(--vibe-token)` string. */
  color: string;
  /**
   * The pill's ground. Vibe's own `-selected` variants are used rather than a mixed or
   * alpha-blended colour, so each tint is a value Vibe defines for BOTH themes instead of
   * one we compute from a token that changes underneath it.
   */
  ground: string;
  /**
   * The words in the pill. Short enough for a dense grid, and phrased as what IS — the
   * tooltip carries the consequence.
   */
  label: string;
  /** Shown on hover. Says what is wrong, in the user's terms — not the enum name. */
  tooltip: string;
}

/**
 * Colour carries the split that matters: these six states are not equally alarming.
 *
 * `value_drift` and `name_drift` are informational — data disagrees, nothing is broken —
 * so they take --warning-color. The three existence states imply a remedy that CHANGES
 * something outside our system, and are coloured by what that remedy does: creating a
 * missing subitem is a write we want noticed (--warning-color), and clearing an orphaned
 * record is a deletion (--negative-color). An unlinked subitem is ADDITIVE to connect —
 * SIMB starts syncing a subitem that exists and creates its copies, nothing is overwritten
 * or deleted — so it takes --primary-color. That is the colour it had before Phase 3b Task 8
 * greyed it out, and Task 8 was right to at the time: Phase 3a Decision 3 gave the row no
 * action, and an accent beside nothing to click is an affordance that isn't there. Since
 * 2026-09-11 the row carries Connect (subitemScanAdapter.ts's `terminalActions`), so colour
 * and copy agree again in the other direction. Decision 3 ruled out adopting the subitem
 * INTO AN EXISTING group, which would need name-matching; Connect mints a NEW group, which
 * needs none.
 *
 * Tooltips state the consequence rather than naming the state — they are the only words
 * the user gets, since the Status cell is icon-only.
 */
export const STATE_VISUALS: Record<DiscrepancyState, StateVisual> = {
  ok: {
    icon: Check,
    color: "var(--positive-color)",
    ground: "var(--positive-color-selected)",
    label: "",
    tooltip: "Every board is synced with this value.",
  },
  value_drift: {
    icon: Warning,
    color: "var(--primary-text-color)",
    ground: "var(--warning-color-selected)",
    label: "",
    tooltip: "Boards hold different values for this column. Syncing overwrites the others.",
  },
  name_drift: {
    icon: Warning,
    color: "var(--warning-color)",
    ground: "var(--warning-color-selected)",
    label: "",
    tooltip: "The name differs across boards. Syncing renames the others.",
  },
  missing: {
    icon: Alert,
    color: "var(--warning-color)",
    ground: "var(--warning-color-selected)",
    label: "",
    tooltip: "A synced board has no matching subitem. Syncing will create one there.",
  },
  // Warning, not negative: the scan runs with the VIEWER's Monday token, and
  // Monday omits an item both when it is permanently deleted and when the
  // token cannot see it — indistinguishable from the scan alone. So this state
  // is "SIMB couldn't find it", not "it is gone". The server re-checks with
  // another user's access before it deletes anything (2026-09-10).
  orphaned_record: {
    icon: Remove,
    color: "var(--warning-color)",
    ground: "var(--warning-color-selected)",
    label: "",
    tooltip:
      "SIMB can't see the Monday subitem behind this link with your permissions. It may have been deleted, or it may be hidden from you. Resolving re-checks with another user's access and only removes a link whose subitem is confirmed gone.",
  },
  unlinked_subitem: {
    icon: Link,
    color: "var(--primary-color)",
    // Accent ground: the row carries Connect, an additive remedy (see the header comment).
    ground: "var(--primary-selected-color)",
    label: "",
    // Consequence first, then remedy — and in the user's terms. The old copy ("SIMB does not
    // manage it. Re-run sync setup to include it.") named our system instead of their problem,
    // and sent them to a board-wide setup wizard for a single subitem.
    tooltip:
      "This subitem isn't part of the sync yet, so changes to it won't reach your other boards. Connect it to start syncing it across the linked boards.",
  },
};

interface StateIndicatorProps {
  state: DiscrepancyState;
}

/**
 * The Status cell's badge: a tinted pill carrying the state's name.
 *
 * It used to be icon-only, on the reasoning that the row's action button already carried the
 * words. That held for one row in isolation and failed down a column: three of the six states
 * share the same warning yellow, and `value_drift` and `name_drift` share the icon as well —
 * so a reader scanning the column saw one state where there were three, and had to hover each
 * row to tell them apart. The label is what distinguishes them; the tint groups them.
 *
 * Text and icon both take `--primary-text-color` rather than the state colour. Vibe's
 * `-selected` grounds are pale in the light theme, and the state colour on top of its own
 * tint (warning yellow on pale yellow) is close to unreadable. The GROUND carries the
 * colour coding; the words carry the meaning.
 */
const StateIndicator: React.FC<StateIndicatorProps> = ({ state }) => {
  const visual = STATE_VISUALS[state];
  if (!visual) return null;

  const Icon = visual.icon;

  return (
    <Tooltip content={visual.tooltip}>
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "var(--spacing-xs)",
          height: 22,
          width: 22,
          padding: "0",
          borderRadius: 100,
          background: visual.ground,
          color: "var(--primary-text-color)",
          fontWeight: 500,
          whiteSpace: "nowrap",
        }}
        aria-label={visual.tooltip}
        role="img"
        data-testid={`state-indicator-${state}`}
      >
        <Icon />
      </span>
    </Tooltip>
  );
};

export default StateIndicator;
